import { FlowItemConditions, FlowItemDataMap } from '../TypeDefs';

export type FlowConnectorEdge = { fromNodeId: string, toNodeId: string };

/** Merge-node condition labels (AND/OR) used for corridor / OR-resolution rules */
export type FlowItemMergeRef = { nodeId: string; conditionText?: string };

export const toFlowMergeRefs = (flowItems: { nodeId: string; conditionText?: string }[]): FlowItemMergeRef[] =>
  flowItems.map(({ nodeId, conditionText }) => ({ nodeId, conditionText }));

/**
 * Directed reachability on the journey connector graph.
 */
export const canReachNode = (from: string, to: string, edges: FlowConnectorEdge[]): boolean => {
  if (from === to) {
    return true;
  }
  const adj = new Map<string, string[]>();
  edges.forEach((e) => {
    if (!adj.has(e.fromNodeId)) {
      adj.set(e.fromNodeId, []);
    }
    adj.get(e.fromNodeId)!.push(e.toNodeId);
  });
  const visited = new Set<string>();
  const stack = [from];
  while (stack.length > 0) {
    const n = stack.pop()!;
    if (n === to) {
      return true;
    }
    if (visited.has(n)) {
      continue;
    }
    visited.add(n);
    (adj.get(n) ?? []).forEach((next) => stack.push(next));
  }
  return false;
};

const shortestPathLen = (start: string, end: string, edges: FlowConnectorEdge[]): number | null => {
  if (start === end) {
    return 0;
  }
  const adj = new Map<string, string[]>();
  edges.forEach((e) => {
    if (!adj.has(e.fromNodeId)) {
      adj.set(e.fromNodeId, []);
    }
    adj.get(e.fromNodeId)!.push(e.toNodeId);
  });
  const queue: string[] = [start];
  const dist = new Map<string, number>([[start, 0]]);
  while (queue.length > 0) {
    const n = queue.shift()!;
    const d = dist.get(n) ?? 0;
    for (const next of adj.get(n) ?? []) {
      if (next === end) {
        return d + 1;
      }
      if (!dist.has(next)) {
        dist.set(next, d + 1);
        queue.push(next);
      }
    }
  }
  return null;
};

const forkToMergeDistance = (forkId: string, mergeId: string, edges: FlowConnectorEdge[]): number | null => {
  const outs = edges.filter((e) => e.fromNodeId === forkId);
  let best: number | null = null;
  outs.forEach((o) => {
    const d = shortestPathLen(o.toNodeId, mergeId, edges);
    if (d !== null) {
      const total = 1 + d;
      best = best === null ? total : Math.min(best, total);
    }
  });
  return best;
};

/**
 * For parallel merge M, finds the unique fork F whose outgoing branches all reach M
 * and jointly cover all immediate predecessors of M (phase-1 topology).
 */
export const findDivergenceForkForMerge = (mergeNodeId: string, flowConnectors: FlowConnectorEdge[]): string | null => {
  const incoming = flowConnectors.filter((c) => c.toNodeId === mergeNodeId);
  if (incoming.length < 2) {
    return null;
  }
  const preds = incoming.map((c) => c.fromNodeId);
  const fromIds = new Set(flowConnectors.map((c) => c.fromNodeId));
  const candidates: string[] = [];
  fromIds.forEach((F) => {
    const outs = flowConnectors.filter((c) => c.fromNodeId === F);
    if (outs.length < 2) {
      return;
    }
    const children = outs.map((c) => c.toNodeId);
    if (!children.every((child) => canReachNode(child, mergeNodeId, flowConnectors))) {
      return;
    }
    const predsCovered = preds.every((pred) =>
      children.some((child) => pred === child || canReachNode(child, pred, flowConnectors)));
    if (!predsCovered) {
      return;
    }
    const eachChildUsed = children.every((child) =>
      preds.some((pred) => pred === child || canReachNode(child, pred, flowConnectors)));
    if (!eachChildUsed) {
      return;
    }
    candidates.push(F);
  });
  if (candidates.length === 0) {
    return null;
  }
  let best: string | null = null;
  let bestDist = Infinity;
  candidates.forEach((F) => {
    const d = forkToMergeDistance(F, mergeNodeId, flowConnectors);
    if (d !== null && d < bestDist) {
      bestDist = d;
      best = F;
    }
  });
  return best;
};

const hasStoredCondExpr = (entry: { rawExpression?: string, builderExpression?: string } | undefined): boolean =>
  !!(entry?.rawExpression?.trim() || entry?.builderExpression?.trim());

export const countForkOutboundCondExpressions = (
  forkNodeId: string,
  flowConnectors: FlowConnectorEdge[],
  flowItemDataMap: FlowItemDataMap
): number => {
  let n = 0;
  flowConnectors.forEach((c) => {
    if (c.fromNodeId !== forkNodeId) {
      return;
    }
    const entry = flowItemDataMap[c.toNodeId]?.condExpr?.[forkNodeId];
    if (hasStoredCondExpr(entry)) {
      n += 1;
    }
  });
  return n;
};

const collectOrMergeNodeIds = (flowItems: FlowItemMergeRef[]): Set<string> => new Set(
  flowItems
    .filter((fi) => fi.conditionText === FlowItemConditions.OR)
    .map((fi) => fi.nodeId)
);

/**
 * Connectors where conditional expressions must be disabled for each AND merge M:
 * every edge u→v with F→u and v→M for the AND divergence fork F, except edges that lie only on an inner
 * OR “diamond”: for each OR merge O on a path F→…→O→…→M, let F_o = findDivergenceForkForMerge(O); if F_o
 * exists, edges with canReach(F_o,u) and canReach(v,O) (strictly between inner fork and OR merge) stay allowed.
 */
export const buildForkOutboundForbiddenKeys = (
  andMergeNodeIds: Set<string>,
  flowConnectors: FlowConnectorEdge[],
  flowItems: FlowItemMergeRef[]
): Set<string> => {
  const forbidden = new Set<string>();
  const orMergeNodeIds = collectOrMergeNodeIds(flowItems);
  const orDivergenceForkCache = new Map<string, string | null>();
  const getOrDivergenceFork = (O: string): string | null => {
    if (!orDivergenceForkCache.has(O)) {
      orDivergenceForkCache.set(O, findDivergenceForkForMerge(O, flowConnectors));
    }
    return orDivergenceForkCache.get(O) ?? null;
  };

  andMergeNodeIds.forEach((M) => {
    const F = findDivergenceForkForMerge(M, flowConnectors);
    if (!F) {
      return;
    }
    flowConnectors.forEach((c) => {
      const u = c.fromNodeId;
      const v = c.toNodeId;
      if (!u) {
        return;
      }
      if (!canReachNode(F, u, flowConnectors)) {
        return;
      }
      if (!canReachNode(v, M, flowConnectors)) {
        return;
      }
      const excused = [...orMergeNodeIds].some((O) => {
        if (O === M) {
          return false;
        }
        if (!canReachNode(F, O, flowConnectors)) {
          return false;
        }
        if (!canReachNode(O, M, flowConnectors)) {
          return false;
        }
        const forkForOr = getOrDivergenceFork(O);
        if (!forkForOr) {
          return false;
        }
        return canReachNode(forkForOr, u, flowConnectors) && canReachNode(v, O, flowConnectors);
      });
      if (!excused) {
        forbidden.add(`${u}\0${v}`);
      }
    });
  });
  return forbidden;
};

/**
 * Direct edges into any AND/OR convergence node (immediate predecessors of a merge).
 * Always forbidden for conditional expressions in the UI, regardless of inner-OR corridor exceptions.
 */
export const buildMergeDirectInboundForbiddenKeys = (
  flowConnectors: FlowConnectorEdge[],
  flowItems: FlowItemMergeRef[]
): Set<string> => {
  const mergeIds = new Set(
    flowItems
      .filter((fi) => fi.conditionText === FlowItemConditions.AND || fi.conditionText === FlowItemConditions.OR)
      .map((fi) => fi.nodeId)
  );
  const keys = new Set<string>();
  flowConnectors.forEach((c) => {
    if (!c.fromNodeId || !mergeIds.has(c.toNodeId)) {
      return;
    }
    keys.add(`${c.fromNodeId}\0${c.toNodeId}`);
  });
  return keys;
};

/**
 * Forbidden keys for disabling conditional expressions in the UI: AND-merge corridors plus
 * every direct inbound edge to an AND or OR merge.
 */
export const buildCondExpForbiddenKeysForUi = (
  andMergeNodeIds: Set<string>,
  flowConnectors: FlowConnectorEdge[],
  flowItems: FlowItemMergeRef[]
): Set<string> => {
  const merged = new Set(buildForkOutboundForbiddenKeys(andMergeNodeIds, flowConnectors, flowItems));
  buildMergeDirectInboundForbiddenKeys(flowConnectors, flowItems).forEach((k) => merged.add(k));
  return merged;
};

/**
 * Connectors whose stored expressions are cleared when a given node becomes (or is) an AND merge:
 * AND corridor for that merge plus direct inbound edges into that merge only.
 */
export const buildForbiddenKeysForAndMergeClear = (
  mergeNodeId: string,
  flowConnectors: FlowConnectorEdge[],
  flowItems: FlowItemMergeRef[]
): Set<string> => {
  const keys = buildForkOutboundForbiddenKeys(new Set([mergeNodeId]), flowConnectors, flowItems);
  flowConnectors.forEach((c) => {
    if (c.fromNodeId && c.toNodeId === mergeNodeId) {
      keys.add(`${c.fromNodeId}\0${c.toNodeId}`);
    }
  });
  return keys;
};

/**
 * Removes stored conditional expressions on every connector forbidden for the given AND merge.
 */
export const clearCondExprForAndMergeCorridor = (
  mergeNodeId: string,
  flowConnectors: FlowConnectorEdge[],
  flowItems: FlowItemMergeRef[],
  flowItemDataMap: FlowItemDataMap
): FlowItemDataMap => {
  const forbidden = buildForbiddenKeysForAndMergeClear(mergeNodeId, flowConnectors, flowItems);
  if (forbidden.size === 0) {
    return flowItemDataMap;
  }
  let next: FlowItemDataMap | null = null;
  forbidden.forEach((key) => {
    const [fromId, toId] = key.split('\0');
    const map = next ?? flowItemDataMap;
    const src = map[toId];
    if (!src?.condExpr?.[fromId]) {
      return;
    }
    if (!next) {
      next = { ...flowItemDataMap };
    }
    const { [fromId]: _removed, ...restCond } = next[toId].condExpr;
    next[toId] = { ...next[toId], condExpr: restCond };
  });
  return next ?? flowItemDataMap;
};

/**
 * Counts stored expressions on connectors that would be forbidden when mergeNodeId is AND.
 */
export const countStoredCondExprForMergeCorridor = (
  mergeNodeId: string,
  flowConnectors: FlowConnectorEdge[],
  flowItems: FlowItemMergeRef[],
  flowItemDataMap: FlowItemDataMap
): number => {
  const forbidden = buildForbiddenKeysForAndMergeClear(mergeNodeId, flowConnectors, flowItems);
  let n = 0;
  forbidden.forEach((key) => {
    const [fromId, toId] = key.split('\0');
    if (hasStoredCondExpr(flowItemDataMap[toId]?.condExpr?.[fromId])) {
      n += 1;
    }
  });
  return n;
};

/**
 * Ingress event labels (from {@link FlowItemDataMap} `ingressEvent`) for downstream nodes that have
 * a stored conditional expression on a connector that would be cleared for this AND merge.
 */
export const listClearableCondExprIngressLabelsForMerge = (
  mergeNodeId: string,
  flowConnectors: FlowConnectorEdge[],
  flowItems: FlowItemMergeRef[],
  flowItemDataMap: FlowItemDataMap
): string[] => {
  const forbidden = buildForbiddenKeysForAndMergeClear(mergeNodeId, flowConnectors, flowItems);
  const labels: string[] = [];
  const seen = new Set<string>();
  forbidden.forEach((key) => {
    const [fromId, toId] = key.split('\0');
    const entry = flowItemDataMap[toId]?.condExpr?.[fromId];
    if (!hasStoredCondExpr(entry)) {
      return;
    }
    const label = flowItemDataMap[toId]?.ingressEvent ?? fromId;
    if (!seen.has(label)) {
      seen.add(label);
      labels.push(label);
    }
  });
  labels.sort();
  return labels;
};

export const getAndMergeNodeIdsFromFlowItems = (
  flowItems: { nodeId: string, conditionText?: string }[]
): Set<string> => new Set(
  flowItems
    .filter((fi) => fi.conditionText === FlowItemConditions.AND)
    .map((fi) => fi.nodeId)
);

export const isCondExpForbiddenOnConnector = (
  fromNodeId: string,
  toNodeId: string,
  flowConnectors: FlowConnectorEdge[],
  andMergeNodeIds: Set<string>,
  flowItems: FlowItemMergeRef[]
): boolean => {
  const forbidden = buildCondExpForbiddenKeysForUi(andMergeNodeIds, flowConnectors, flowItems);
  return forbidden.has(`${fromNodeId}\0${toNodeId}`);
};
